import React, { useState } from 'react';

interface Campaign {
  _id: string;
  name: string;
  enabled: boolean;
  loading?: boolean;
  post_count?: number;
  slot_selected_groups?: any[];
  max_daily_posts?: number;
}

interface CampaignStats {
  totalPostsAllTime?: number;
  scheduledToday?: number;
  postedToday?: number;
  totalToday?: number;
}

interface MobileCampaignSwitcherProps {
  campaigns: Campaign[];
  selectedCampaign: Campaign | null;
  onCampaignSelect: (campaign: Campaign) => void;
  onEnableCampaign: (campaign: Campaign, enabled: boolean) => void;
  onDeleteCampaign: (campaign: Campaign) => void;
  onCreateCampaign: () => void;
  onSelectGroups: () => void;
  onScrollToPosts: () => void;
  campaignStats: { [key: string]: CampaignStats };
  onEditCampaignName?: (campaignId: string, currentName: string, maxPosts?: number) => void;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

export const MobileCampaignSwitcher: React.FC<MobileCampaignSwitcherProps> = ({
  campaigns,
  selectedCampaign,
  onCampaignSelect,
  onEnableCampaign,
  onDeleteCampaign,
  onCreateCampaign,
  onSelectGroups,
  onScrollToPosts,
  campaignStats,
  onEditCampaignName,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Same test GroupsWrapper uses: the onboarded tour's first step, before a campaign has
  // been picked. Read from the URL rather than threaded through as a prop so this stays
  // a leaf component.
  const inCampaignSelectStep = (() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('tut') === 'onboarded' && (sp.get('step') === null || sp.get('step') === '0');
  })();
  // Once they've picked one during the tour, the bottom bar stops reopening the sheet:
  // the step is satisfied, and re-picking re-navigates and re-renders under the banner,
  // which on a phone reads as the tutorial jumping around.
  // The lock is on the BAR, not the rows — a sheet that's already open stays fully
  // usable, so nobody gets stranded looking at a list they can't act on. Closing it
  // (pick, ✕, or backdrop) is what ends the choosing. Normal service resumes after
  // the step.
  const campaignChosen = /^\/campaigns\/[A-Za-z0-9]/.test(window.location.pathname);
  const lockCampaignChoice = inCampaignSelectStep && campaignChosen;

  const handleCampaignClick = (campaign: Campaign) => {
    onCampaignSelect(campaign);
    setIsSheetOpen(false);
  };

  const handleOverlayClick = () => {
    setIsSheetOpen(false);
  };

  // Show bar only when campaigns exist
  const showBar = campaigns && campaigns.length > 0;

  // Return null when no campaigns - empty state is shown in GroupsWrapper
  if (!showBar && !selectedCampaign) {
    return null;
  }

  return (
    <>
      {/* Sticky Bottom Bar */}
      <div
        className={`mobile-campaign-bar${lockCampaignChoice ? ' tut-locked' : ''}`}
        onClick={() => { if (!lockCampaignChoice) setIsSheetOpen(true) }}
      >
        <div className="mobile-campaign-bar-content">
          <div className="mobile-campaign-bar-right">
            <span className={`status-dot ${selectedCampaign?.enabled ? 'active' : 'paused'}`}></span>
            <div className="mobile-campaign-bar-info">
              <span className="mobile-campaign-bar-label">קמפיין נוכחי</span>
              <span className="mobile-campaign-bar-name">{selectedCampaign?.name || 'בחר קמפיין'}</span>
            </div>
          </div>
          <div className="mobile-campaign-bar-left">
            <div className="mobile-campaign-bar-switch">
              <span className="mobile-campaign-bar-switch-text">החלף קמפיין</span>
              <span className="mobile-campaign-bar-chevron">⇅</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isSheetOpen && (
        <div
          className="campaign-sheet-overlay"
        />
      )}

      {/* Bottom Sheet */}
      <div className={`campaign-sheet ${isSheetOpen ? 'show' : ''}`}>
        <div className="campaign-sheet-header">
          <h3>מעבר בין קמפיינים</h3>
          <button
            className="campaign-sheet-close"
            onClick={() => setIsSheetOpen(false)}
          >
            ✕
          </button>
        </div>

        <div className="campaign-sheet-list">
          {/* Sort campaigns to show selected campaign first */}
          {[...campaigns].sort((a, b) => {
            if (a._id === selectedCampaign?._id) return -1;
            if (b._id === selectedCampaign?._id) return 1;
            return 0;
          }).map((camp) => {
            const isSelected = selectedCampaign?._id === camp._id;
            const isPaused = !camp.enabled;
            const campGroupCount = camp.slot_selected_groups?.length || 0;
            const stats = campaignStats[camp._id];

            return (
              <div
                key={camp._id}
                className={`campaign-sheet-item ${isSelected ? 'selected' : ''} ${isPaused ? 'paused' : ''}`}
                // During the tutorial's "pick a campaign" step the whole row selects,
                // including the greyed-out buttons: the per-row actions are disabled
                // there, so a tap on them would otherwise land on nothing and read as
                // the row being unresponsive. Only the inner block is clickable the rest
                // of the time, so the edit/delete/toggle buttons keep working normally.
                onClick={inCampaignSelectStep ? () => handleCampaignClick(camp) : undefined}
              >
                <div
                  className="campaign-sheet-item-main"
                  onClick={(e) => { e.stopPropagation(); handleCampaignClick(camp) }}
                >
                  <div className="campaign-sheet-item-info-wrapper">
                    <span className={`status-dot ${isPaused ? 'paused' : 'active'}`}></span>
                    <div className="campaign-sheet-item-info">
                      <span className="campaign-sheet-item-name">{camp.name}</span>
                      <span className="campaign-sheet-item-stats">
                        👥 {campGroupCount} קבוצות · 📊 {camp.post_count || 0} פוסטים
                      </span>
                      <span className="campaign-sheet-item-stats">
                        📈 {formatNumber(stats?.totalPostsAllTime || 0)} פורסמו · 📅 {stats?.scheduledToday || 0} מתוזמנים היום
                      </span>
                    </div>
                  </div>
                  {isSelected && <span className="campaign-sheet-checkmark">✓</span>}
                </div>
                <div className="campaign-sheet-item-buttons">
                  <label className="campaign-sheet-toggle" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!isPaused}
                      disabled={camp.loading}
                      onChange={() => onEnableCampaign(camp, isPaused)}
                    />
                    <span className={`campaign-sheet-toggle-slider ${camp.loading ? 'loading' : ''}`}></span>
                  </label>
                  {onEditCampaignName && (
                    <button
                      className="campaign-sheet-btn edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCampaignName(camp._id, camp.name, camp.max_daily_posts);
                        setIsSheetOpen(false);
                      }}
                    >
                      ✏️
                    </button>
                  )}
                  <button
                    className="campaign-sheet-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCampaign(camp);
                      setIsSheetOpen(false);
                    }}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Create New Campaign Button */}
        <button
          className="campaign-sheet-create-btn"
          onClick={() => {
            setIsSheetOpen(false);
            onCreateCampaign();
          }}
        >
          + צור קמפיין חדש
        </button>
      </div>
    </>
  );
};
